/**
 * Renders the catalog / cheat-sheets trail into the filter bar's title slot.
 *
 * Pure renderer — it binds nothing. `CatalogNavigationClass` owns the one delegated click listener on
 * the page and calls in here; My Training holds its own instance in a second bundle.
 */
export default class CatalogBreadcrumb {
    constructor() {
        this.classList = {
            hook: 'js-catalog-title',
            linkHook: 'js-topic-breadcrumb-link',
            rootHook: 'js-catalog-root-link',
            topicHook: 'open-topic__js',
            treeHook: 'js-catalog-tree',
            filterBar: 'filter-bar',
            topicOpen: 'is-topic-open',
            link: 'topic-breadcrumb__link',
            sep: 'topic-breadcrumb__sep',
            current: 'topic-breadcrumb__current',
        };

        this.nav = document.querySelector(`.${this.classList.hook}`);
        this.rootUrl = this.nav?.dataset.rootUrl || '';
        this.leafSuffix = this.nav?.dataset.leafSuffix || '';
        // The root crumb keeps the words the server chose, so a reload and a click never disagree.
        this.rootLabel = this.nav?.firstElementChild?.textContent.trim() || '';
    }

    /**
     * Replaces the trail.
     *
     * @param {Array<{label: string, href?: string, topic?: string, level?: string}>} items
     *      Root first, current level last.
     */
    render(items) {
        if (!this.nav) {
            return;
        }

        const {
            linkHook, rootHook, link, sep, current,
        } = this.classList;
        const nodes = [];

        items.forEach((item, index) => {
            if (index > 0) {
                const separator = document.createElement('span');
                separator.className = sep;
                separator.setAttribute('aria-hidden', 'true');
                separator.textContent = '/';
                nodes.push(separator);
            }

            if (index === items.length - 1) {
                const leaf = document.createElement('span');
                leaf.className = current;
                leaf.setAttribute('aria-current', 'page');
                leaf.textContent = item.label;
                nodes.push(leaf);
                return;
            }

            const anchor = document.createElement('a');
            anchor.className = `${link} ${item.topic ? linkHook : rootHook}`;
            anchor.href = item.href || '';
            if (item.topic) {
                anchor.dataset.topic = item.topic;
                anchor.dataset.level = item.level || 'topic';
            }
            anchor.textContent = item.label;
            nodes.push(anchor);
        });

        this.nav.replaceChildren(...nodes);
    }

    /**
     * Renders the trail for a drill-down target.
     *
     * @param {HTMLElement} element Any `.open-topic__js` — a sidebar link, a grid tile, or the sidebar
     *      twin a crumb resolved to.
     */
    renderForTopic(element) {
        if (!this.nav || !element) {
            return;
        }

        const {
            topicHook, treeHook, filterBar, topicOpen,
        } = this.classList;
        const isSubtopic = element.dataset.level === 'subtopic';
        const label = isSubtopic && this.leafSuffix
            ? `${element.dataset.name || ''} ${this.leafSuffix}`.trim()
            : (element.dataset.name || '');

        const items = [{ label: this.rootLabel, href: this.rootUrl }];

        // The sidebar tree is not a superset of the grid (the QR sidebar drops childless topics, and a
        // topic without sub-topics renders course tiles carrying a course id), so the parent crumb is
        // only offered when the clicked level really has a twin in the tree.
        const twin = document.querySelector(
            `.${treeHook} .${topicHook}[data-topic="${element.dataset.topic}"]`,
        );
        const parent = twin
            ? twin.closest('ul')?.closest('li')?.querySelector('[data-level="topic"]')
            : null;

        if (parent) {
            items.push({
                label: parent.dataset.name || '',
                href: parent.getAttribute('href') || '',
                topic: parent.dataset.topic,
                level: 'topic',
            });
        }

        items.push({ label });
        this.render(items);

        this.nav.closest(`.${filterBar}`)?.classList.add(topicOpen);
    }

    /**
     * Renders a single-crumb trail — My Training and the learning path, which have no topic tree.
     *
     * @param {string} text The only crumb.
     */
    renderLabel(text) {
        this.render([{ label: text }]);
    }

    /**
     * Puts the trail back to its root state and lets the search field return with it.
     */
    renderRoot() {
        const { filterBar, topicOpen } = this.classList;

        this.renderLabel(this.rootLabel);
        this.nav?.closest(`.${filterBar}`)?.classList.remove(topicOpen);
    }

    /**
     * @returns {boolean} Whether the page was rendered at the section root — the only state whose grid
     *      the server ships in full, and so the only one a click can restore without a reload.
     */
    isAtRoot() {
        const { filterBar, topicOpen } = this.classList;

        return !!this.nav && !this.nav.closest(`.${filterBar}`)?.classList.contains(topicOpen);
    }
}
